
import { useEffect, useRef, useState } from "react";
import { getCustomer } from "../../api/customerApi";
import type { CustomerOption } from "../../api/customerApi";

interface CustomerPickerProps {
  query: string;
  onQueryChange: (value: string) => void;
  onSelectCustomer: (customer: CustomerOption) => void;
  selectedCustomerId: number | null;
  onClearSelection: () => void;
}

export default function CustomerPicker({
  query,
  onQueryChange,
  onSelectCustomer,
  selectedCustomerId,
  onClearSelection,
}: CustomerPickerProps) {
  const [customers, setCustomers] = useState<CustomerOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);

  const pickerRef = useRef<HTMLDivElement>(null);

  // =========================
  // Load Customer
  // =========================
  useEffect(() => {
    const loadCustomers = async () => {
      try {
        setLoading(true);

        const response = await getCustomer({
          page: 1,
          limit: 100,
        });

        const customerList = response?.data?.customerList ?? [];

        setCustomers(customerList);
      } catch (error) {
        console.error("Failed to load customers:", error);
        setCustomers([]);
      } finally {
        setLoading(false);
      }
    };

    loadCustomers();
  }, []);

  // =========================
  // Click Outside
  // =========================
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        pickerRef.current &&
        !pickerRef.current.contains(event.target as Node)
      ) {
        setShowDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // =========================
  // Filter Customer
  // =========================
  const filteredCustomers = customers.filter((customer) => {
    const keyword = query.trim().toLowerCase();

    if (!keyword) {
      return true;
    }

    return (
      customer.customerName?.toLowerCase().includes(keyword) ||
      customer.companyName?.toLowerCase().includes(keyword) ||
      customer.phone?.toLowerCase().includes(keyword) ||
      customer.email?.toLowerCase().includes(keyword)
    );
  });

  // =========================
  // Select Customer
  // =========================
  const handleSelectCustomer = (customer: CustomerOption) => {
    onSelectCustomer(customer);
    setShowDropdown(false);
  };

  // =========================
  // Clear Customer
  // =========================
  const handleClearCustomer = () => {
    onClearSelection();
    setShowDropdown(false);
  };

  return (
    <div ref={pickerRef} className="relative mb-6">
      <label className="mb-1 block text-sm font-medium text-gray-600">
        Customer
      </label>

      <div className="relative">
        <input
          type="text"
          value={query}
          onChange={(e) => {
            onQueryChange(e.target.value);
            setShowDropdown(true);
          }}
          onFocus={() => {
            if (selectedCustomerId === null) {
              setShowDropdown(true);
            }
          }}
          placeholder="Search customer..."
          className="w-full rounded-lg border border-gray-200 px-3 py-2 pr-20 text-sm outline-none focus:border-blue-400"
          disabled={selectedCustomerId !== null}
        />

        {selectedCustomerId !== null && (
          <button
            type="button"
            onClick={handleClearCustomer}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-sm font-medium text-red-500 hover:text-red-700"
          >
            Clear
          </button>
        )}

        {selectedCustomerId === null && loading && (
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400">
            Loading...
          </span>
        )}
      </div>

      {/* Dropdown */}
      {showDropdown && selectedCustomerId === null && (
        <div className="absolute z-50 mt-1 max-h-60 w-full overflow-y-auto rounded-lg border border-gray-200 bg-white shadow-lg">
          {loading ? (
            <div className="px-4 py-3 text-sm text-gray-400">
              Loading customer...
            </div>
          ) : filteredCustomers.length === 0 ? (
            <div className="px-4 py-3 text-sm text-gray-400">
              No customer found.
            </div>
          ) : (
            filteredCustomers.map((customer) => (
              <button
                key={customer.id}
                type="button"
                onClick={() => handleSelectCustomer(customer)}
                className="block w-full border-b border-gray-100 px-4 py-3 text-left last:border-b-0 hover:bg-gray-50"
              >
                <div className="text-sm font-medium text-gray-800">
                  {customer.customerName}
                </div>

                {customer.companyName && (
                  <div className="mt-0.5 text-xs text-gray-500">
                    {customer.companyName}
                  </div>
                )}

                <div className="mt-1 text-xs text-gray-400">
                  {customer.phone || customer.email || "-"}
                </div>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}

